import { Component, OnDestroy } from '@angular/core';
import { NbAuthResult, NbAuthService } from '@nebular/auth';
import {ActivatedRoute, Router} from '@angular/router';
import { map, takeUntil, withLatestFrom } from "rxjs/operators";
import {from, interval, of, Subject, switchMap, takeWhile} from 'rxjs';
import {StoreService} from '../../../../../core/src/lib/store/store.service';
import {ApiService} from '../../../../../core/src/lib/api/api.service';

@Component({
  selector: 'nb-playground-oauth2-callback',
  template: `
    <nb-layout>
      <nb-layout-column>
        <div>{{message}}</div>
        <div *ngIf="isPolling" class="mt-3">
          <nb-spinner [status]="'primary'" [size]="'medium'"></nb-spinner>
        </div>
        <div *ngIf="authorizedLocations.length > 0" class="mt-3">
          <h5>Authorized Franchise Locations:</h5>
          <ul>
            <li *ngFor="let loc of authorizedLocations">{{ loc.name || loc.merchantID }}</li>
          </ul>
        </div>
      </nb-layout-column>
    </nb-layout>
  `,
})
export class OAuth2CallbackComponent implements OnDestroy {

  private destroy$ = new Subject<void>();

  public message = 'Authenticating...';
  public isPolling = false;
  public authorizedLocations: any[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private store: StoreService,
    private api: ApiService
  ) {
    const requestOrganization = localStorage.getItem('requestOrganization') === 'true';
    localStorage.removeItem('requestOrganization');

    this.route.queryParams.pipe(takeUntil(this.destroy$), switchMap((params) => {
      if (params['code'] && params['state'] === localStorage.getItem('state')) {
        this.message = 'Connecting accounts...'
        return this.store.authorizeSeller(params['code'], requestOrganization).pipe(withLatestFrom(this.store.user$),switchMap(([response, user]) => {
          if (response.success && user) {
            return this.store.updateUser(user.id, user.first_name, user.last_name, user.email, user.role.id, user.active, false).pipe(map(() => {
              if (requestOrganization) {
                this.message = 'Authorization is being processed. This may take up to 1 minute.';
                this.pollAuthorizationStatus();
              } else {
                this.message = 'Account authorized. Redirecting...';
                setTimeout(() => {
                  this.router.navigateByUrl('/').then(() => window.location.reload());
                }, 1000);
              }
              return;
            }))
          } else {
            this.message = 'Could not connect accounts. Please try again.';
            setTimeout(() => {
              this.router.navigate(['auth','oauth2']);
            }, 1000);
            return;
          }
        }))
      } else {
        this.message = 'Account not authorized. Please try again.';
        setTimeout(() => {
          this.router.navigate(['auth','oauth2']);
        }, 1000);
        return;
      }
    })).subscribe(() => localStorage.removeItem('state'));
  }

  private pollAuthorizationStatus(): void {
    this.isPolling = true;
    let attempts = 0;
    const maxAttempts = 12; // Poll for up to ~1 minute (every 5 seconds)

    interval(5000).pipe(
      takeUntil(this.destroy$),
      takeWhile(() => attempts < maxAttempts),
      switchMap(() => {
        attempts++;
        return this.api.getAuthorizationStatus();
      })
    ).subscribe({
      next: (status) => {
        if (status?.complete) {
          this.isPolling = false;
          this.authorizedLocations = status.locations || [];
          this.message = 'Organization authorization complete!';
          setTimeout(() => {
            this.router.navigateByUrl('/').then(() => window.location.reload());
          }, 3000);
        }
      },
      error: () => {
        this.isPolling = false;
        this.message = 'Account authorized. Redirecting...';
        setTimeout(() => {
          this.router.navigateByUrl('/').then(() => window.location.reload());
        }, 1000);
      },
      complete: () => {
        if (this.isPolling) {
          // Max attempts reached without completion
          this.isPolling = false;
          this.message = 'Account authorized. Organization setup may still be processing. Redirecting...';
          setTimeout(() => {
            this.router.navigateByUrl('/').then(() => window.location.reload());
          }, 2000);
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
